import { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import api, { apiError } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { ROLE_LABELS } from "../lib/format";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Badge } from "../components/ui/badge";
import { Card, CardContent } from "../components/ui/card";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "../components/ui/dialog";
import { KeyRound, AlertTriangle, Loader2, Percent } from "lucide-react";
import { toast } from "sonner";

export default function Settings() {
  const { user, logout } = useAuth();
  const navigate = useNavigate();
  const [f, setF] = useState({ old_password: "", new_password: "", confirm: "" });
  const [busy, setBusy] = useState(false);
  const [resetOpen, setResetOpen] = useState(false);
  const [resetPw, setResetPw] = useState("");
  const [resetBusy, setResetBusy] = useState(false);
  const [taxPct, setTaxPct] = useState("");
  const [taxBusy, setTaxBusy] = useState(false);

  useEffect(() => {
    if (user.role !== "master") return;
    api.get("/settings").then((r) => setTaxPct(String(+((r.data.tax_rate || 0) * 100).toFixed(3)))).catch(() => {});
  }, [user.role]);

  const saveTax = async () => {
    const pct = parseFloat(String(taxPct).replace(",", "."));
    if (isNaN(pct) || pct < 0 || pct > 100) { toast.error("Masukkan tarif pajak yang valid (0 - 100%)"); return; }
    setTaxBusy(true);
    try {
      const r = await api.put("/settings", { tax_rate: pct / 100 });
      setTaxPct(String(+((r.data.tax_rate || 0) * 100).toFixed(3)));
      toast.success("Tarif pajak berhasil disimpan");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setTaxBusy(false); }
  };

  const submit = async () => {
    if (!f.old_password || !f.new_password) { toast.error("Lengkapi password"); return; }
    if (f.new_password !== f.confirm) { toast.error("Konfirmasi password baru tidak cocok"); return; }
    setBusy(true);
    try {
      await api.post("/auth/change-password", { old_password: f.old_password, new_password: f.new_password });
      toast.success("Password berhasil diubah");
      setF({ old_password: "", new_password: "", confirm: "" });
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  const doReset = async () => {
    if (!resetPw) { toast.error("Masukkan password untuk konfirmasi"); return; }
    setResetBusy(true);
    try {
      await api.post("/reset-database", { password: resetPw });
      toast.success("Seluruh data toko berhasil dihapus");
      setResetOpen(false); setResetPw("");
      await logout();
      navigate("/login");
    } catch (e) { toast.error(apiError(e.response?.data?.detail)); }
    finally { setResetBusy(false); }
  };

  return (
    <div className="space-y-5 max-w-2xl" data-testid="settings-page">
      <div>
        <h1 className="text-2xl font-bold font-display text-slate-900">Pengaturan Akun</h1>
        <p className="text-sm text-slate-500">Profil & keamanan akun Anda</p>
      </div>

      <Card className="border-slate-200">
        <CardContent className="p-6 flex items-center gap-4">
          <div className="h-14 w-14 rounded-2xl bg-slate-900 flex items-center justify-center text-white text-xl font-bold">{user.name?.[0]}</div>
          <div>
            <div className="font-semibold text-lg">{user.name}</div>
            <div className="text-sm text-slate-500 font-mono">{user.username}</div>
            <Badge className="mt-1 bg-orange-100 text-orange-700 hover:bg-orange-100">{ROLE_LABELS[user.role]}</Badge>
          </div>
        </CardContent>
      </Card>

      <Card className="border-slate-200">
        <CardContent className="p-6 space-y-4">
          <div className="flex items-center gap-2 font-semibold"><KeyRound className="h-4 w-4 text-orange-500" /> Ubah Password</div>
          <p className="text-sm text-slate-500 -mt-2">Wajib verifikasi password lama.</p>
          <div className="space-y-1"><Label>Password Lama</Label><Input type="password" value={f.old_password} onChange={(e) => setF({ ...f, old_password: e.target.value })} data-testid="old-password-input" /></div>
          <div className="space-y-1"><Label>Password Baru</Label><Input type="password" value={f.new_password} onChange={(e) => setF({ ...f, new_password: e.target.value })} data-testid="new-password-input" /></div>
          <div className="space-y-1"><Label>Konfirmasi Password Baru</Label><Input type="password" value={f.confirm} onChange={(e) => setF({ ...f, confirm: e.target.value })} data-testid="confirm-password-input" /></div>
          <Button onClick={submit} disabled={busy} className="bg-orange-500 hover:bg-orange-600" data-testid="change-password-button">Ubah Password</Button>
        </CardContent>
      </Card>

      {user.role === "master" && (
        <Card className="border-slate-200" data-testid="tax-settings-card">
          <CardContent className="p-6 space-y-4">
            <div className="flex items-center gap-2 font-semibold"><Percent className="h-4 w-4 text-orange-500" /> Tarif Pajak Penjualan</div>
            <p className="text-sm text-slate-500 -mt-2">Dipakai untuk menghitung pajak pada Laporan Laba (Laba Bersih = Laba Kotor − Pajak − Pengeluaran Kas).</p>
            <div className="flex items-end gap-3">
              <div className="space-y-1">
                <Label>Tarif Pajak (%)</Label>
                <div className="relative w-40">
                  <Input type="number" step="0.001" min="0" max="100" value={taxPct} onChange={(e) => setTaxPct(e.target.value)} className="pr-8" data-testid="tax-rate-input" />
                  <span className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 text-sm">%</span>
                </div>
              </div>
              <Button onClick={saveTax} disabled={taxBusy} className="bg-orange-500 hover:bg-orange-600" data-testid="save-tax-rate-button">
                {taxBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Simpan"}
              </Button>
            </div>
            <p className="text-xs text-slate-400">Contoh: isi <b>0.5</b> untuk tarif 0,5%.</p>
          </CardContent>
        </Card>
      )}

      {user.role === "master" && (
        <Card className="border-rose-300 bg-rose-50/40" data-testid="danger-zone">
          <CardContent className="p-6 space-y-3">
            <div className="flex items-center gap-2 font-semibold text-rose-700"><AlertTriangle className="h-4 w-4" /> Zona Berbahaya — Reset Database</div>
            <p className="text-sm text-slate-600">
              Menghapus <b>permanen</b> seluruh data toko: barang & HPP, stok & opname, transaksi & laba,
              pelanggan & hutang, serta karyawan & kasbon/payroll. Akun pengguna tetap ada.
              Tindakan ini <b>tidak dapat dibatalkan</b>.
            </p>
            <Button onClick={() => setResetOpen(true)} variant="outline"
              className="border-rose-400 text-rose-700 hover:bg-rose-100" data-testid="open-reset-database-button">
              <AlertTriangle className="h-4 w-4 mr-1.5" /> Hapus Semua Pendataan
            </Button>
          </CardContent>
        </Card>
      )}

      <Dialog open={resetOpen} onOpenChange={(o) => { if (!o) { setResetOpen(false); setResetPw(""); } }}>
        <DialogContent className="sm:max-w-md" data-testid="reset-database-dialog">
          <DialogHeader><DialogTitle className="text-rose-700 flex items-center gap-2"><AlertTriangle className="h-5 w-5" /> Konfirmasi Hapus Total</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <p className="text-sm text-slate-700">Apakah Anda yakin ingin menghapus <b>seluruh data toko</b>? Tindakan ini <b>tidak dapat dibatalkan</b>.</p>
            <div className="space-y-1">
              <Label>Masukkan ulang password Anda</Label>
              <Input type="password" value={resetPw} onChange={(e) => setResetPw(e.target.value)} placeholder="Password Master" data-testid="reset-password-input" />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => { setResetOpen(false); setResetPw(""); }} data-testid="reset-cancel-button">Batal</Button>
            <Button onClick={doReset} disabled={resetBusy} className="bg-rose-600 hover:bg-rose-700" data-testid="confirm-reset-database-button">
              {resetBusy ? <Loader2 className="h-4 w-4 animate-spin" /> : "Hapus Permanen"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
